import React, { useState, useEffect } from 'react';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { login, signInWithOAuth, isAuthenticated } from '../../services/authUtils';

const LoginPage = ({ onLoginSuccess, returnUrl = '/' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in - if so, redirect immediately to avoid bounce
    const checkAuth = async () => {
      if (await isAuthenticated()) {
        console.log('[LOGIN PAGE] User already authenticated, redirecting immediately');
        // Get return URL from query params
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get('return') || '/';
        
        // If return URL is a proxied service, go directly there
        if (returnUrl.startsWith('/diet-app') || returnUrl.startsWith('/job-search')) {
          console.log('[LOGIN PAGE] Redirecting directly to service:', returnUrl);
          window.location.href = returnUrl;
          return;
        }
        
        // Otherwise call success callback
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }
    };
    checkAuth();
  }, [onLoginSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('[LOGIN PAGE] Attempting login...');
      const result = await login(email, password);
      console.log('[LOGIN PAGE] Login successful, result:', result);
      
      // Wait a moment for auth state to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Call success callback
      if (onLoginSuccess) {
        console.log('[LOGIN PAGE] Calling onLoginSuccess callback');
        onLoginSuccess();
      } else {
        console.warn('[LOGIN PAGE] No onLoginSuccess callback provided');
      }
    } catch (err) {
      console.error('[LOGIN PAGE] Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = async (provider) => {
    setError('');
    setIsLoading(true);
    try {
      await signInWithOAuth(provider, window.location.origin);
      // OAuth will redirect, so we don't need to navigate
    } catch (err) {
      setError(err.message || `Failed to sign in with ${provider}`);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center font-serif text-3xl font-semibold tracking-tight text-stone-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <button
              type="button"
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('page', 'signup');
                if (returnUrl) url.searchParams.set('return', returnUrl);
                window.history.pushState({}, '', url);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              create a new account
            </button>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="field field-on-page field-icon field-top relative block focus:z-10"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="field field-on-page field-icon field-bottom relative block focus:z-10"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <button
                type="button"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('page', 'forgot-password');
                  if (returnUrl) url.searchParams.set('return', returnUrl);
                  window.history.pushState({}, '', url);
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-full text-white bg-stone-900 hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Sign in
                </>
              )}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-paper text-stone-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleOAuthLogin('google')}
                disabled={isLoading}
                className="w-full inline-flex justify-center py-2 px-4 border border-stone-300 rounded-xl bg-white text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50"
              >
                <span>Google</span>
              </button>
              <button
                type="button"
                onClick={() => handleOAuthLogin('github')}
                disabled={isLoading}
                className="w-full inline-flex justify-center py-2 px-4 border border-stone-300 rounded-xl bg-white text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50"
              >
                <span>GitHub</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
